/**
 * MfaStepUpModal — modal global que aparece quando uma operacao protegida
 * por `RequiresMfaRecent` / `HasPermissionAutoMfa` retorna 403
 * "Esta operacao exige verificacao MFA recente.".
 *
 * Acoplado ao `AuthContext`:
 *  - Abre automaticamente quando `mfaStepUpOpen === true` (setado pelo
 *    interceptor do `request()` quando detecta o 403).
 *  - Submete o codigo via `verifyMfaStepUp(codigo)`.
 *  - Em sucesso, chama `completeMfaStepUp(true)` — a `request()` original
 *    eh refeita transparente e a UI da tela continua o fluxo normal.
 *  - Em cancelamento, `cancelMfaStepUp()` — a request original rejeita
 *    com o erro 403, deixando o caller decidir o que fazer.
 *
 * Mantido visualmente em sintonia com `MfaVerify` (login 2-step) reusando
 * os styles `Button`/`Input`/`Message`/`Field`/`Label` de `pages/Auth/styles`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiCheckCircle, FiKey, FiShield, FiX } from "react-icons/fi";
import styled from "styled-components";

import { useAuth } from "../../context/AuthContext";
import { MiniSpinner } from "../LoadingMarcenaria";
import {
  Button,
  Field,
  Input,
  Label,
  Message,
} from "../../pages/Auth/styles";

const MODO_TOTP = "totp";
const MODO_RECOVERY = "recovery";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 16px;
`;

const Card = styled.div`
  width: min(420px, 100%);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.infoSurface};
  color: ${({ theme }) => theme.infoText};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Titulo = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.textPrimary};
`;

const Subtitulo = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.4;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
`;

const ToggleLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

export default function MfaStepUpModal() {
  const {
    mfaStepUpOpen,
    verifyMfaStepUp,
    completeMfaStepUp,
    cancelMfaStepUp,
    registrarConsumidorDeStepUp,
  } = useAuth();

  // Anuncia ao AuthContext que existe alguem para atender a fila de
  // step-up. Sem este registro, o interceptor deixa a request cair no
  // erro normal em vez de esperar por um modal que nao esta na tela.
  useEffect(() => registrarConsumidorDeStepUp?.(), [registrarConsumidorDeStepUp]);

  const [codigo, setCodigo] = useState("");
  const [modo, setModo] = useState(MODO_TOTP);
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  // Quando o modal abre/fecha, limpa estado local para nao vazar entre
  // sessoes de step-up.
  useEffect(() => {
    if (!mfaStepUpOpen) {
      setCodigo("");
      setModo(MODO_TOTP);
      setErro("");
      setLoading(false);
    }
  }, [mfaStepUpOpen]);

  const codigoCompleto = useMemo(() => {
    if (modo === MODO_TOTP) return codigo.length === 6;
    return codigo.length === 9 && codigo.includes("-");
  }, [codigo, modo]);

  function handleCodigoChange(event) {
    const raw = event.target.value;
    if (modo === MODO_TOTP) {
      setCodigo(raw.replace(/\D/g, "").slice(0, 6));
    } else {
      const limpo = raw
        .toUpperCase()
        .replace(/[^A-Z0-9-]/g, "")
        .slice(0, 9);
      setCodigo(limpo);
    }
    if (erro) setErro("");
  }

  function trocarModo(novo) {
    setModo(novo);
    setCodigo("");
    setErro("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!codigoCompleto || loading) return;
    setErro("");
    setLoading(true);
    try {
      await verifyMfaStepUp(codigo);
      completeMfaStepUp(true);
    } catch (err) {
      setErro(err?.message || "Código inválido. Tente novamente.");
      setLoading(false);
    }
  }

  function handleCancelar() {
    if (loading) return;
    cancelMfaStepUp();
  }

  if (!mfaStepUpOpen) return null;

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfa-step-up-titulo"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) cancelMfaStepUp();
      }}
    >
      <Card>
        <Header>
          <IconBadge>
            <FiShield size={18} />
          </IconBadge>
          <div>
            <Titulo id="mfa-step-up-titulo">Confirmar identidade</Titulo>
            <Subtitulo>
              Esta operação exige verificação MFA recente. Informe o código
              do seu app autenticador para continuar — sem precisar fazer
              login novamente.
            </Subtitulo>
          </div>
        </Header>

        <Form onSubmit={handleSubmit}>
          {erro && (
            <Message $tone="danger" aria-live="polite">
              {erro}
            </Message>
          )}

          <Field>
            <Label>
              {modo === MODO_TOTP
                ? "Código do autenticador"
                : "Código de recuperação"}
            </Label>
            <Input
              type="text"
              value={codigo}
              onChange={handleCodigoChange}
              autoComplete="one-time-code"
              autoFocus
              inputMode={modo === MODO_TOTP ? "numeric" : "text"}
              placeholder={modo === MODO_TOTP ? "123456" : "XXXX-YYYY"}
              aria-label={
                modo === MODO_TOTP
                  ? "Código de 6 dígitos do autenticador"
                  : "Código de recuperação no formato XXXX-YYYY"
              }
              maxLength={modo === MODO_TOTP ? 6 : 9}
              disabled={loading}
              style={{
                fontFamily: "var(--font-mono, ui-monospace, monospace)",
                fontSize: "1.2rem",
                letterSpacing: "0.25em",
                textAlign: "center",
              }}
            />
          </Field>

          <div>
            {modo === MODO_TOTP ? (
              <ToggleLink
                type="button"
                onClick={() => trocarModo(MODO_RECOVERY)}
                disabled={loading}
              >
                <FiKey size={12} /> Usar código de recuperação
              </ToggleLink>
            ) : (
              <ToggleLink
                type="button"
                onClick={() => trocarModo(MODO_TOTP)}
                disabled={loading}
              >
                <FiShield size={12} /> Usar código do autenticador
              </ToggleLink>
            )}
          </div>

          <Footer>
            <Button
              type="button"
              $variant="secondary"
              onClick={handleCancelar}
              disabled={loading}
            >
              <FiX size={14} /> Cancelar
            </Button>
            <Button type="submit" disabled={!codigoCompleto || loading}>
              {loading ? (
                <MiniSpinner size={14} label="Verificando" />
              ) : (
                <FiCheckCircle size={14} />
              )}
              {loading ? "Verificando..." : "Confirmar"}
            </Button>
          </Footer>
        </Form>
      </Card>
    </Overlay>
  );
}
